import { ReactElement, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Typography, useMediaQuery } from '@mui/material';

import {
  DISCORD_LINK,
  DOCS_LINK,
  TELEGRAM_LINK,
  TERMS_LINK,
  X_LINK,
  YOUTUBE_LINK,
} from 'modules/common/const';
import { featureConfig } from 'modules/common/featureConfig';
import { DashboardRoutesConfig } from 'modules/dashboard/Routes';
import { DelegateStakingRoutesConfig } from 'modules/delegateStaking/Routes';
import { useGlobalTranslation } from 'modules/i18n';

import logoIcon from '../../assets/logo.svg';
import { translation } from './translation';
import { useStyles } from './useStyles';

const currentYear = new Date().getFullYear();

export function Footer(): ReactElement {
  const { t, keys } = useGlobalTranslation(translation);
  const { classes, theme, cx } = useStyles();
  const isMd = useMediaQuery(theme.breakpoints.down('md'));

  const socials = useMemo(
    () => [
      {
        title: t(keys.socials.twitter),
        link: X_LINK,
      },
      {
        title: t(keys.socials.youtube),
        link: YOUTUBE_LINK,
      },
      {
        title: t(keys.socials.telegram),
        link: TELEGRAM_LINK,
      },
      {
        title: t(keys.socials.discord),
        link: DISCORD_LINK,
      },
    ],
    [
      keys.socials.discord,
      keys.socials.telegram,
      keys.socials.twitter,
      keys.socials.youtube,
      t,
    ],
  );

  const docsContent = (
    <>
      {DOCS_LINK && (
        <Typography
          className={classes.link}
          component="a"
          href={DOCS_LINK}
          rel="noreferrer"
          target="_blank"
        >
          {t(keys.docs)}
        </Typography>
      )}

      {TERMS_LINK && (
        <Typography
          className={classes.link}
          component="a"
          href={TERMS_LINK}
          rel="noreferrer"
          target="_blank"
        >
          {t(keys.terms)}
        </Typography>
      )}
    </>
  );

  return (
    <footer className={classes.root}>
      <div className={classes.info}>
        <Link
          className={classes.logoLink}
          to={
            featureConfig.isDashboardEnabled
              ? DashboardRoutesConfig.dashboard.generatePath()
              : DelegateStakingRoutesConfig.staking.generatePath()
          }
        >
          <img alt="MATSTAKE" className={classes.logo} src={logoIcon} />
        </Link>

        <div className={classes.divider} />

        <Typography
          color={theme => theme.palette.grey[200]}
          fontWeight={400}
          variant="body1"
        >
          {t(keys.allRights, { year: currentYear })}
        </Typography>
      </div>

      <div className={cx(classes.docs, classes.centeredLink)}>
        {docsContent}
      </div>

      <div className={classes.socials}>
        {socials.map(({ title, link }) => (
          <Typography
            key={title}
            className={classes.link}
            component="a"
            href={link}
            rel="noreferrer"
            target="_blank"
          >
            {title}
          </Typography>
        ))}
      </div>

      {isMd && <div className={classes.docs}>{docsContent}</div>}
    </footer>
  );
}
